import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Loader2, MapPin, Sparkles, Trophy, User2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

// Leaderboards — a shared route with tab-switched cohorts (campers or
// campsites) and a metric chip (xp / stamps / checkins).
// Backend: GET /leaderboard/campers?metric=&limit= and .../campsites.
export default function Leaderboards() {
  const { api, user } = useAuth();
  const [cohort, setCohort] = useState("campers"); // campers | campsites
  const [metric, setMetric] = useState("xp");      // xp | stamps | checkins
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null); setError(null);
    api.get(`/leaderboard/${cohort}`, { params: { metric, limit: 50 } })
      .then(({ data }) => {
        if (cancelled) return;
        setRows(Array.isArray(data) ? data : (data?.items || []));
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load leaderboard.");
      });
    return () => { cancelled = true; };
  }, [api, cohort, metric]);

  return (
    <div className="flex flex-col gap-5" data-testid="leaderboards-page">
      <header>
        <div className="text-xs font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Leaderboards
        </div>
        <h1 className="text-2xl md:text-3xl font-extrabold mt-1" style={{ color: "var(--on-surface)" }}>
          Standings
        </h1>
      </header>

      {/* Cohort tabs */}
      <div className="flex rounded-full p-1 self-start"
           style={{ background: "color-mix(in srgb, var(--brand) 8%, transparent)" }}>
        {[
          { k: "campers", label: "Campers", icon: User2 },
          { k: "campsites", label: "Campsites", icon: MapPin },
        ].map((c) => (
          <button key={c.k} onClick={() => setCohort(c.k)}
                  data-testid={`lb-cohort-${c.k}`}
                  className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-widest px-4 py-2 rounded-full transition"
                  style={{
                    background: cohort === c.k ? "var(--brand)" : "transparent",
                    color: cohort === c.k ? "white" : "var(--brand)",
                  }}>
            <c.icon className="w-3.5 h-3.5" /> {c.label}
          </button>
        ))}
      </div>

      {/* Metric chips */}
      <div className="flex items-center gap-2">
        {["xp", "stamps", "checkins"].map((m) => (
          <button key={m} onClick={() => setMetric(m)}
                  data-testid={`lb-metric-${m}`}
                  className="text-[11px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full"
                  style={{
                    background: metric === m ? "color-mix(in srgb, var(--premium) 20%, transparent)" : "color-mix(in srgb, var(--brand) 8%, transparent)",
                    color: metric === m ? "var(--premium)" : "var(--brand)",
                  }}>
            {m}
          </button>
        ))}
      </div>

      {error && <div className="card-elev p-4" style={{ color: "var(--premium)" }}>{error}</div>}
      {!rows && !error && (
        <div className="card-elev p-6 flex items-center gap-3" style={{ color: "var(--on-surface-muted)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {/* Personal position banner — campers cohort only */}
      {cohort === "campers" && rows && (
        <YouAreRanked rows={rows} user={user} metric={metric} />
      )}

      {rows && rows.length === 0 && (
        <div className="card-elev p-8 text-center" data-testid="lb-empty">
          <Trophy className="w-8 h-8 mx-auto opacity-40" style={{ color: "var(--on-surface-muted)" }} />
          <div className="mt-2 text-lg font-extrabold" style={{ color: "var(--on-surface)" }}>Nothing to show yet</div>
          <div className="text-sm" style={{ color: "var(--on-surface-muted)" }}>Standings will appear as adventures roll in.</div>
        </div>
      )}
      {rows && rows.length > 0 && (
        <div className="card-elev overflow-hidden divide-y" style={{ borderColor: "var(--border-c)" }}>
          {rows.map((r, i) => cohort === "campers"
            ? <CamperRow key={`${i}-${(r.user||r).user_id || i}`} r={r} i={i} metric={metric} meId={user?.user_id} />
            : <CampsiteRow key={`${i}-${(r.cs||r).campsite_id || i}`} r={r} i={i} metric={metric} />)}
        </div>
      )}
    </div>
  );
}

// Personal position banner. Prefers the mobile backend's `me` payload
// (rank + value in one shot), and falls back to scanning the loaded page
// so the banner still works if the endpoint variant is missing.
function YouAreRanked({ rows, user, metric }) {
  const meId = user?.user_id;
  const found = useMemo(() => {
    if (!meId || !Array.isArray(rows)) return null;
    const idx = rows.findIndex((r) => ((r.user || r).user_id) === meId);
    if (idx < 0) return null;
    const row = rows[idx];
    const u = row.user || row;
    const value = row.value ?? u.xp ?? 0;
    return { rank: idx + 1, value };
  }, [rows, meId]);

  if (!meId) return null;

  if (!found) {
    return (
      <div
        data-testid="lb-you-unranked"
        className="card-elev p-4 flex items-center gap-3"
        style={{ borderColor: "var(--border-c)" }}
      >
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
             style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)" }}>
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-extrabold" style={{ color: "var(--on-surface)" }}>
            You’re not in the top 50 yet
          </div>
          <div className="text-xs" style={{ color: "var(--on-surface-muted)" }}>
            Check in at more campsites to climb the {metric.toUpperCase()} board.
          </div>
        </div>
        <Link
          to="/explore"
          data-testid="lb-you-explore"
          className="text-[11px] font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full"
          style={{ background: "var(--brand)", color: "white" }}
        >
          Explore
        </Link>
      </div>
    );
  }

  const isTop3 = found.rank <= 3;
  return (
    <div
      data-testid="lb-you-ranked"
      className="card-elev p-4 flex items-center gap-3"
      style={{
        borderColor: "var(--premium)",
        background: "color-mix(in srgb, var(--premium) 8%, transparent)",
      }}
    >
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-sm font-extrabold"
           style={{
             background: isTop3 ? "var(--premium)" : "var(--brand)",
             color: "white",
           }}>
        {found.rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-extrabold uppercase tracking-widest" style={{ color: "var(--on-surface-muted)" }}>
          Your position
        </div>
        <div className="text-sm font-extrabold truncate" style={{ color: "var(--on-surface)" }}>
          You are ranked #{found.rank}
          {isTop3 && <span className="ml-2" style={{ color: "var(--premium)" }}>· Top 3 🏆</span>}
        </div>
      </div>
      <div className="text-sm font-extrabold text-right shrink-0" style={{ color: "var(--brand)" }}>
        {Number(found.value).toLocaleString()}
        <span className="ml-1 text-[10px] font-bold" style={{ color: "var(--on-surface-muted)" }}>{metric}</span>
      </div>
    </div>
  );
}

function Rank({ i }) {
  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-extrabold shrink-0"
         style={{
           background: i < 3 ? "color-mix(in srgb, var(--premium) 20%, transparent)" : "color-mix(in srgb, var(--brand) 10%, transparent)",
           color: i < 3 ? "var(--premium)" : "var(--brand)",
         }}>
      {i + 1}
    </div>
  );
}

function CamperRow({ r, i, metric, meId }) {
  const u = r.user || r;
  const value = r.value ?? u.xp ?? 0;
  const label = u.consent_shared === false || u.is_anonymous ? "Anonymous Camper" : (u.nickname || u.name || "—");
  const avatar = u.avatar_camper || u.avatar_data || null;
  const isMe = !!meId && u.user_id === meId;
  return (
    <div
      className="flex items-center gap-3 px-4 py-3"
      data-testid={isMe ? `lb-camper-${i}-me` : `lb-camper-${i}`}
      style={isMe ? { background: "color-mix(in srgb, var(--brand) 8%, transparent)" } : undefined}
    >
      <Rank i={i} />
      {avatar ? <img src={avatar} className="w-9 h-9 rounded-full object-cover" alt="" />
             : <div className="w-9 h-9 rounded-full flex items-center justify-center font-extrabold"
                    style={{ background: "var(--brand)", color: "white" }}>{(label[0] || "?").toUpperCase()}</div>}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-extrabold truncate flex items-center gap-1.5" style={{ color: "var(--on-surface)" }}>
          {isMe ? "You" : label}
          {isMe && (
            <span className="text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                  style={{ background: "var(--brand)", color: "white" }}>
              You
            </span>
          )}
        </div>
        <div className="text-[11px]" style={{ color: "var(--on-surface-muted)" }}>Lvl {u.level ?? 1}</div>
      </div>
      <div className="text-sm font-extrabold" style={{ color: "var(--brand)" }}>
        {Number(value).toLocaleString()} <span className="text-[10px] font-bold" style={{ color: "var(--on-surface-muted)" }}>{metric}</span>
      </div>
    </div>
  );
}

function CampsiteRow({ r, i, metric }) {
  const c = r.cs || r;
  const value = r.value ?? c.xp ?? 0;
  return (
    <Link to={`/campsites/${c.campsite_id}`}
          className="flex items-center gap-3 px-4 py-3 hover:opacity-80"
          data-testid={`lb-campsite-${i}`}>
      <Rank i={i} />
      {c.photo ? <img src={c.photo} className="w-9 h-9 rounded-lg object-cover" alt="" />
              : <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                     style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)" }}>
                  <MapPin className="w-4 h-4" />
                </div>}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-extrabold truncate" style={{ color: "var(--on-surface)" }}>{c.name}</div>
        <div className="text-[11px]" style={{ color: "var(--on-surface-muted)" }}>Lvl {c.level ?? 1} · {c.state || "—"}</div>
      </div>
      <div className="text-sm font-extrabold" style={{ color: "var(--brand)" }}>
        {Number(value).toLocaleString()} <span className="text-[10px] font-bold" style={{ color: "var(--on-surface-muted)" }}>{metric}</span>
      </div>
      <ChevronRight className="w-4 h-4 opacity-40" />
    </Link>
  );
}
